/**
 * Feeds 搜索模式共用工具函数
 *
 * 支持三种搜索模式：
 * - fuzzy（模糊搜索）：使用 feeds/comments 接口，search_content 字段，不隐藏 keyword
 * - exact（精准匹配）：使用 feeds/search 接口，search_data 字段，隐藏 keyword
 * - extend（多语种拓展匹配）：使用 feeds/search 接口，search_data 字段，隐藏 keyword
 */

/** 搜索模式类型 */
export type SearchMode = 'fuzzy' | 'exact' | 'extend';

/**
 * 判断当前是否为模糊搜索模式
 */
export const isFuzzySearchMode = (
  matchModel: string,
  hasSearch: boolean,
): boolean => {
  return hasSearch && matchModel === 'fuzzy';
};

/**
 * 判断当前是否为精准/拓展搜索模式（需要调用搜索接口）
 */
export const isExactOrExtendSearchMode = (
  matchModel: string,
  hasSearch: boolean,
): boolean => {
  return hasSearch && matchModel !== 'fuzzy';
};

/**
 * 获取搜索字段名
 * - 模糊搜索：search_content
 * - 精准/拓展搜索：search_data
 */
export const getSearchFieldName = (matchModel: string): string => {
  return matchModel === 'fuzzy' ? 'search_content' : 'search_data';
};

/**
 * 构建搜索参数对象
 * @param matchModel 当前匹配模式
 * @param search 搜索文本
 * @param hasSearch 是否有搜索内容
 * @returns 搜索参数字段对象
 */
export const buildSearchParams = (
  matchModel: string,
  search: string,
  hasSearch: boolean,
): Record<string, string> => {
  if (!hasSearch) return {};
  const fieldName = getSearchFieldName(matchModel);
  return { [fieldName]: search };
};

/**
 * 判断是否应该隐藏 keyword 板块
 * - 精准/拓展搜索：隐藏 keyword
 * - 模糊搜索/非搜索：不隐藏 keyword
 */
export const shouldHideKeyword = (
  matchModel: string,
  hasSearch: boolean,
): boolean => {
  return isExactOrExtendSearchMode(matchModel, hasSearch);
};

/**
 * 判断是否应该使用搜索接口（feeds/search）
 * - 精准/拓展搜索：使用搜索接口
 * - 模糊搜索：使用 feeds/comments 接口
 */
export const shouldUseSearchApi = (
  matchModel: string,
  hasSearch: boolean,
): boolean => {
  return isExactOrExtendSearchMode(matchModel, hasSearch);
};
